// Turns raw bars into what traders see: % paths, order blocks, liquidity sweeps, estimated order
// flow, volume profiles, strength scores, sector breadth, rankings and stock-page levels.
import { curveFraction, type Market } from './market';
import type {
  Analysed, Bar, BuildUp, Detail, Flow, LevelKey, LiquidityLevel, OrderBlock, SectorStat, ZoneTouch,
  Session, Snapshot, SRKey, StockSession, Sweep, VolumeProfile, ZonePlan,
} from './types';
import { zoneState } from './types';

export const pctOf = (price: number, base: number) => (price / base - 1) * 100;
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const PROFILE_BINS = 22;
const TOP_N = 20;

/** A usual day's range is about 10 average 5-minute candles (median over 990 NSE stock-days, Oct 2026). */
export const CANDLES_PER_USUAL_DAY = 10;
/** A source candle taller than this many usual candles is "wide": its body becomes the zone. */
export const WIDE_ZONE_CANDLES = 3;
/** A reaction: a close at least this many usual candles away from the zone… */
export const REACTION_CANDLES = 0.5;
/** …within the touch candle and this many after it. */
export const REACTION_BARS = 3;
/** A visit must go at least this far into the zone (usual candles); shallower is a graze. */
export const TOUCH_DEPTH_CANDLES = 0.1;
/** A gap proves the move only if it clears the source candle by this much (usual candles)… */
export const GAP_CLEAR_CANDLES = 0.25;
/** …or comes with a close this far beyond the source candle (usual candles). */
export const GAP_MIN_MOVE_CANDLES = 0.5;
/** Zones confirmed in the last this-many candles of the session are "late" (barely tradable that day). */
export const LATE_CANDLES = 6;
/** A usual candle trading less than this (₹: usual 50-session volume × previous close ÷ candles per day) marks a
 * stock "thin": about the thinnest sixth of NSE stock-days. */
export const THIN_CANDLE_TURNOVER = 4_000_000;
/** Rule version shown with zones; bump when the detector's rules change. */
export const OB_RULES_VERSION = 'ob-2026.10.4';

/** Thinly traded: a usual candle (from the last 50 sessions' volume, so it is known at the open and doesn't
 * change during the day) trades under THIN_CANDLE_TURNOVER, so a single order can make a candle, and a zone. */
export function isThin(s: { avgDayVolume: number; prevClose: number }, sessionBars = 75) {
  return s.avgDayVolume > 0 && (s.avgDayVolume * s.prevClose) / sessionBars < THIN_CANDLE_TURNOVER;
}

/** A stock's usual 5-minute candle (₹) from its usual day range, or null when that isn't known. */
export const usualCandle = (s: { avgDayRangePct?: number | null; prevClose: number }) =>
  s.avgDayRangePct && s.prevClose > 0 ? (s.avgDayRangePct / 100) * s.prevClose / CANDLES_PER_USUAL_DAY : null;

/**
 * Order blocks on finished 5-minute candles. A bullish block is the last red candle before a
 * strong rise that breaks structure; a bearish block, the last green candle before a strong fall.
 *  - Strong: within the next 3 candles price closes beyond the block by at least one candle (the
 *    smaller of the session's average candle so far and the stock's usual candle, so big opening
 *    candles don't set the bar), or the move leaves a gap (a candle whose low is above the high of
 *    the candle two before it; mirrored for falls) that clears the source candle by a quarter of a
 *    usual candle or comes with a close at least half a usual candle beyond it.
 *  - Break of structure: within the next 6 candles a close beyond a level above the block (below,
 *    for bearish): the most recent confirmed swing high (low), or the opening-range high (low) once
 *    the first 15 minutes are done. Wicks don't count; with neither level, there is no block. The
 *    zone exists from that close (confirmIdx); a close beyond the far edge before it voids the block.
 *  - Zone: the source candle's full range; its body if the candle is over 3 usual candles ("wide").
 *  - Life, from the candle after confirmation: once price has left the zone, each return at least a
 *    tenth of a usual candle deep is a touch (shallower is a graze).
 *    A touch reacted if, within it and the next 3 candles, a close moved half a usual candle away
 *    from the zone; otherwise it failed (the zone stays). A finished candle closing beyond the far
 *    edge breaks the zone.
 * Every threshold is fixed when the zone is confirmed, so a zone shown at any time never changes
 * because of later candles except through its life events. `forming` = the last bar (index t-1) is
 * still trading and is ignored. `usual` = the stock's usual 5-minute candle (usualCandle); without
 * it, the session's average candle up to confirmation is used.
 */
export function orderBlocks(bars: Bar[], t: number, forming = false, usual: number | null = null): OrderBlock[] {
  const out: OrderBlock[] = [];
  const done = forming ? t - 1 : t;
  // The opening range takes 3 candles; a block needs at least one candle after it.
  if (done < 4) return out;
  // avgBefore(j): average range of candles 0..j-1 (known when candle j starts).
  const rangeSums = [0];
  for (let i = 0; i < done; i++) rangeSums.push(rangeSums[i] + (bars[i].h - bars[i].l));
  const avgBefore = (j: number) => (j > 0 ? rangeSums[Math.min(j, done)] / Math.min(j, done) : bars[0].h - bars[0].l);
  const volSums = [0];
  for (let i = 0; i < done; i++) volSums.push(volSums[i] + bars[i].v);

  // Swing points (2 bars each side) and the bar where each is confirmed.
  const swingHighs: { p: number; confirmed: number }[] = [], swingLows: { p: number; confirmed: number }[] = [];
  for (let i = 2; i < done - 2; i++) {
    let isHigh = true, isLow = true;
    for (let j = i - 2; j <= i + 2; j++) {
      if (j === i) continue;
      if (bars[j].h >= bars[i].h) isHigh = false;
      if (bars[j].l <= bars[i].l) isLow = false;
    }
    if (isHigh) swingHighs.push({ p: bars[i].h, confirmed: i + 2 });
    if (isLow) swingLows.push({ p: bars[i].l, confirmed: i + 2 });
  }
  const orHigh = Math.max(bars[0].h, bars[1].h, bars[2].h), orLow = Math.min(bars[0].l, bars[1].l, bars[2].l);

  // Structure on the far side of block i that a move from it must close beyond: the most recent
  // swing confirmed by then, and the opening-range high/low (a break of it counts from bar 3,
  // once the first 15 minutes are complete).
  const structure = (i: number, up: boolean) => {
    const b = bars[i];
    const beyond = (p: number) => (up ? p > b.h : p < b.l);
    const levels: { p: number; from: number; kind: 'swing' | 'opening-range' }[] = [];
    const list = up ? swingHighs : swingLows;
    for (let k = list.length - 1; k >= 0; k--) if (list[k].confirmed <= i && beyond(list[k].p)) { levels.push({ p: list[k].p, from: i + 1, kind: 'swing' }); break; }
    if (done > 3 && beyond(up ? orHigh : orLow)) levels.push({ p: up ? orHigh : orLow, from: Math.max(i + 1, 3), kind: 'opening-range' });
    return levels;
  };

  // Block at bar i if it is the right colour, the move away is strong and it breaks structure. It is
  // confirmed on the later of the two candles that prove those (confirmIdx), using only candles up
  // to then.
  const qualify = (i: number): OrderBlock | null => {
    const b = bars[i];
    const up = b.c < b.o, down = b.c > b.o;
    if (!up && !down) return null;
    const beyondAt = (j: number) => (up ? bars[j].c - b.h : b.l - bars[j].c);
    // The size of "one candle" for the strength test at j: the smaller of the session's average so far
    // and the usual candle.
    const candleAt = (j: number) => (usual ? Math.min(avgBefore(j), usual) : avgBefore(j));
    // A gap: candle j leaves price untraded between it and candle j-2.
    const gapAt = (j: number) => j >= i + 2 && (up ? bars[j].l > bars[j - 2].h : bars[j].h < bars[j - 2].l);
    // A gap proves the move only if it clears the source candle by a quarter candle, or the close is well beyond it.
    const gapProves = (j: number) => {
      if (!gapAt(j)) return false;
      const u = usual ?? avgBefore(j), clear = up ? bars[j].l - b.h : b.l - bars[j].h;
      return clear >= GAP_CLEAR_CANDLES * u || beyondAt(j) >= GAP_MIN_MOVE_CANDLES * u;
    };
    let strongIdx = -1;
    for (let j = i + 1; j <= Math.min(i + 3, done - 1) && strongIdx < 0; j++) if (beyondAt(j) >= candleAt(j) || gapProves(j)) strongIdx = j;
    if (strongIdx < 0) return null;
    const levels = structure(i, up);
    if (!levels.length) return null;
    for (let j = i + 1; j <= Math.min(i + 6, done - 1); j++) {
      // A close beyond the far edge before the zone is confirmed: the move failed, no block.
      if (up ? bars[j].c < b.l : bars[j].c > b.h) return null;
      if (j < strongIdx) continue;
      const bos = levels.find((L) => { for (let k = Math.max(i + 1, L.from); k <= j; k++) if (up ? bars[k].c > L.p : bars[k].c < L.p) return true; return false; });
      if (!bos) continue;
      // Confirmed at j: the move is strong by j and structure has broken by j.
      // The furthest close beyond the candle (negative: price hasn't closed beyond it; the gap proved the move).
      let move = -Infinity, gap = false;
      for (let k = i + 1; k <= Math.min(i + 3, j); k++) { move = Math.max(move, beyondAt(k)); if (gapProves(k)) gap = true; }
      const unit = usual ?? avgBefore(j + 1);
      const wide = b.h - b.l > WIDE_ZONE_CANDLES * unit;
      const prior = i > 0 ? volSums[i] / i : 0;
      const moveVol = (volSums[j + 1] - volSums[i + 1]) / (j - i);
      return {
        type: up ? 'bull' : 'bear', idx: i,
        top: wide ? Math.max(b.o, b.c) : b.h, bottom: wide ? Math.min(b.o, b.c) : b.l, wide, fullTop: b.h, fullBottom: b.l,
        confirmIdx: j, bosLevel: bos.p, bosKind: bos.kind, displacement: move / unit, gap,
        volRatio: prior > 0 ? moveVol / prior : null, unit,
        mitigated: false, touches: [], retests: 0,
      };
    }
    return null;
  };

  for (let i = 0; i < done - 1; i++) {
    let ob = qualify(i);
    if (!ob) continue;
    // The block is the LAST opposite candle before the move: look a few candles on (past small
    // candles of the other colour) for a later opposite candle that qualifies the same way, but only
    // one confirmed no later, so a zone never changes source once it has been shown.
    for (let k = ob.idx + 1; k <= ob.idx + 3 && k < done - 1; k++) {
      const next = qualify(k);
      if (next && next.type === ob.type && next.confirmIdx <= ob.confirmIdx) ob = next;
    }
    i = ob.idx;
    const bull = ob.type === 'bull';
    const depth = TOUCH_DEPTH_CANDLES * ob.unit;
    const inZone = (c: Bar) => (bull ? c.l <= ob.top - depth : c.h >= ob.bottom + depth); // a real visit
    const outside = (c: Bar) => (bull ? c.l > ob.top : c.h < ob.bottom); // fully clear of the zone
    const reacted = (c: Bar) => (bull ? c.c >= ob.top + REACTION_CANDLES * ob.unit : c.c <= ob.bottom - REACTION_CANDLES * ob.unit);
    let away = outside(bars[ob.confirmIdx]); // price has left the zone since the last visit
    let open: ZoneTouch | null = null;
    for (let j = ob.confirmIdx + 1; j < done; j++) {
      const c = bars[j];
      if (bull ? c.c < ob.bottom : c.c > ob.top) {
        ob.mitigated = true; ob.endIdx = j;
        if (open) { open.outcome = 'failed'; open.resolvedIdx = j; }
        break;
      }
      if (!open && inZone(c) && away) {
        open = { idx: j, outcome: 'testing' };
        ob.touches.push(open);
        away = false;
      }
      if (open) {
        if (reacted(c)) { open.outcome = 'reacted'; open.resolvedIdx = j; open = null; }
        else if (j >= open.idx + REACTION_BARS) { open.outcome = 'failed'; open.resolvedIdx = j; open = null; }
      }
      if (outside(c)) away = true;
    }
    ob.retests = ob.touches.length;
    ob.firstRetestIdx = ob.touches[0]?.idx;
    out.push(ob);
    i += 2; // one zone per impulse
  }
  return out;
}

/**
 * Equal highs and lows: two or more confirmed swing highs (lows) at about the same price. Stop
 * orders pile up just beyond them. `from` is the bar after the pair is confirmed; `untaken` is
 * true while no later finished candle has traded beyond the level.
 */
export function equalLevels(bars: Bar[], done: number, atr: number) {
  const tol = Math.max(atr * 0.1, (bars[0]?.c ?? 0) * 0.0005);
  const pts = (up: boolean) => {
    const list: { p: number; idx: number }[] = [];
    for (let i = 2; i < done - 2; i++) {
      let ok = true;
      for (let j = i - 2; j <= i + 2 && ok; j++) if (j !== i && (up ? bars[j].h >= bars[i].h : bars[j].l <= bars[i].l)) ok = false;
      if (ok) list.push({ p: up ? bars[i].h : bars[i].l, idx: i });
    }
    return list;
  };
  const pairs = (up: boolean) => {
    const list = pts(up), found: { p: number; from: number; untaken: boolean }[] = [];
    for (let b = 1; b < list.length; b++) {
      for (let a = b - 1; a >= 0; a--) {
        if (Math.abs(list[a].p - list[b].p) > tol || list[b].idx - list[a].idx < 3) continue;
        const p = up ? Math.max(list[a].p, list[b].p) : Math.min(list[a].p, list[b].p);
        // Between the two touches price must not have traded beyond the level.
        let clean = true;
        for (let j = list[a].idx + 1; j < list[b].idx && clean; j++) clean = up ? bars[j].h <= p : bars[j].l >= p;
        if (!clean) continue;
        if (found.some((f) => Math.abs(f.p - p) <= tol)) continue;
        const from = list[b].idx + 3;
        let untaken = true;
        for (let j = list[b].idx + 1; j < done && untaken; j++) untaken = up ? bars[j].h <= p : bars[j].l >= p;
        found.push({ p, from, untaken });
        break;
      }
    }
    return found;
  };
  return { highs: pairs(true), lows: pairs(false) };
}

const atrOf = (bars: Bar[], t: number, price: number) => {
  let sum = 0;
  for (let i = 0; i < t; i++) sum += bars[i].h - bars[i].l;
  return sum / t || price * 0.002;
};

/** Volume traded at each price level, split into up-candle (buy) and down-candle (sell) volume. */
export function volumeProfile(bars: Bar[], t: number, lo: number, hi: number): VolumeProfile {
  const buy = new Array<number>(PROFILE_BINS).fill(0);
  const sell = new Array<number>(PROFILE_BINS).fill(0);
  const span = hi - lo || 1;
  for (let i = 0; i < t; i++) {
    const b = bars[i];
    const a = Math.max(0, Math.min(PROFILE_BINS - 1, Math.floor(((b.l - lo) / span) * PROFILE_BINS)));
    const z = Math.max(a, Math.min(PROFILE_BINS - 1, Math.floor(((b.h - lo) / span) * PROFILE_BINS)));
    const share = b.v / (z - a + 1);
    const side = b.c >= b.o ? buy : sell;
    for (let k = a; k <= z; k++) side[k] += share;
  }
  return { buy, sell };
}

interface SweepLevel { key: LevelKey; name: string; p: number; from?: number }

// Liquidity sweep: a candle pokes past an obvious level (where stop orders rest) and closes back
// inside. Above a high it's bearish (breakout buyers trapped); below a low it's bullish.
// Levels: yesterday's high/low, the opening range (first 15 min), and the day's own earlier
// high/low once it is at least 30 minutes old. A close through the level first is a breakout,
// not a sweep; a later close through it marks the sweep as failed. Equal highs/lows (two swing
// highs or lows at about the same price) are levels too. Only finished candles count (`forming`:
// the last bar is still trading).
export function sweepsOf(s: StockSession, bars: Bar[], t0: number, atr: number, forming = false): Sweep[] {
  const out: Sweep[] = [];
  const t = forming ? t0 - 1 : t0;
  if (t < 2) return out;
  // A real sweep pokes meaningfully past the level and closes clearly back inside.
  const poke = atr * 0.15, back = atr * 0.1;
  const avgVol = (j: number) => {
    const from = Math.max(0, j - 12);
    let sum = 0;
    for (let i = from; i < j; i++) sum += bars[i].v;
    return j > from ? sum / (j - from) : 0;
  };
  const mk = (L: SweepLevel, j: number, isHigh: boolean): Sweep => {
    const b = bars[j], av = avgVol(j);
    return {
      type: isHigh ? 'bear' : 'bull', key: L.key, name: L.name, level: L.p, idx: j,
      extreme: isHigh ? b.h : b.l, volMult: av > 0 ? b.v / av : null, failed: false,
    };
  };

  const highs: SweepLevel[] = [], lows: SweepLevel[] = [];
  if (s.prevHigh) highs.push({ key: 'PDH', name: "yesterday's high", p: s.prevHigh, from: 0 });
  if (s.prevLow) lows.push({ key: 'PDL', name: "yesterday's low", p: s.prevLow, from: 0 });
  if (t > 3) {
    highs.push({ key: 'ORH', name: 'the opening-range high', p: Math.max(bars[0].h, bars[1].h, bars[2].h), from: 3 });
    lows.push({ key: 'ORL', name: 'the opening-range low', p: Math.min(bars[0].l, bars[1].l, bars[2].l), from: 3 });
  }
  const scan = (levels: SweepLevel[], isHigh: boolean) => levels.forEach((L) => {
    for (let j = L.from ?? 0; j < t; j++) {
      const b = bars[j];
      if (isHigh ? b.c > L.p : b.c < L.p) break; // closed through: a breakout, not a sweep
      if (isHigh ? b.h > L.p + poke && b.c < L.p - back : b.l < L.p - poke && b.c > L.p + back) {
        out.push(mk(L, j, isHigh));
        break;
      }
    }
  });
  scan(highs, true);
  scan(lows, false);

  let hi = -Infinity, hiIdx = -1, lo = Infinity, loIdx = -1, lastH = -99, lastL = -99;
  for (let j = 0; j < t; j++) {
    const b = bars[j];
    const taken = (type: 'bull' | 'bear') => out.some((x) => x.idx === j && x.type === type);
    if (hiIdx >= 0 && hiIdx <= j - 6 && j - lastH >= 6 && b.h > hi + poke && b.c < hi - back && !taken('bear')) {
      out.push(mk({ key: 'HOD', name: "the day's earlier high", p: hi }, j, true));
      lastH = j;
    }
    if (loIdx >= 0 && loIdx <= j - 6 && j - lastL >= 6 && b.l < lo - poke && b.c > lo + back && !taken('bull')) {
      out.push(mk({ key: 'LOD', name: "the day's earlier low", p: lo }, j, false));
      lastL = j;
    }
    if (b.h > hi) { hi = b.h; hiIdx = j; }
    if (b.l < lo) { lo = b.l; loIdx = j; }
  }

  // Equal highs and lows, scanned last so they never displace the levels above.
  const eq = equalLevels(bars, t, atr);
  const has = (j: number, type: 'bull' | 'bear') => out.some((x) => x.idx === j && x.type === type);
  for (const [levels, isHigh] of [[eq.highs, true], [eq.lows, false]] as const) {
    for (const L of levels) {
      const lvl: SweepLevel = { key: isHigh ? 'EQH' : 'EQL', name: isHigh ? 'the equal highs' : 'the equal lows', p: L.p, from: L.from };
      for (let j = L.from; j < t; j++) {
        const b = bars[j];
        if (isHigh ? b.c > L.p : b.c < L.p) break;
        if (isHigh ? b.h > L.p + poke && b.c < L.p - back : b.l < L.p - poke && b.c > L.p + back) {
          if (!has(j, isHigh ? 'bear' : 'bull')) out.push(mk(lvl, j, isHigh));
          break;
        }
      }
    }
  }

  for (const w of out) {
    for (let k = w.idx + 1; k < t; k++) {
      if (w.type === 'bear' ? bars[k].c > w.level : bars[k].c < w.level) { w.failed = true; w.failIdx = k; break; }
    }
  }
  return out.sort((a, b) => a.idx - b.idx);
}

/**
 * Estimated order flow: buyer- vs seller-initiated volume and the resting order book.
 * Bars without bv/sv (before the feed was running) are left out rather than guessed.
 */
export function flowOf(bars: Bar[], t: number): Flow | null {
  let bvSum = 0, svSum = 0, covered = 0, from = -1, run = 0;
  let book: Bar | null = null;
  const cvd: (number | null)[] = [], delta: (number | null)[] = [];
  for (let i = 0; i < t; i++) {
    const b = bars[i];
    if (b.bv !== undefined || b.sv !== undefined) {
      const bv = b.bv || 0, sv = b.sv || 0;
      if (from < 0) from = i;
      covered++;
      bvSum += bv;
      svSum += sv;
      run += bv - sv;
      delta.push(bv - sv);
      cvd.push(run);
    } else {
      delta.push(null);
      cvd.push(null);
    }
    if (b.bq || b.sq) book = b;
  }
  const total = bvSum + svSum;
  if (!covered || !total) return null;
  let rb = 0, rs = 0;
  for (let i = Math.max(0, t - 6); i < t; i++) { rb += bars[i].bv || 0; rs += bars[i].sv || 0; }
  return {
    from, covered, delta, cvd,
    buyShare: (bvSum / total) * 100,
    deltaPct: ((bvSum - svSum) / total) * 100,
    recentPct: rb + rs > 0 ? ((rb - rs) / (rb + rs)) * 100 : null,
    bidPct: book ? ((book.bq ?? 0) / (((book.bq ?? 0) + (book.sq ?? 0)) || 1)) * 100 : null,
  };
}

function buildUpOf(pct: number, oiChg: number): BuildUp {
  if (pct > 0) return oiChg > 0 ? 'Long build-up' : 'Short covering';
  return oiChg > 0 ? 'Short build-up' : 'Long unwinding';
}

/** Bars that patterns may read: before the closing auction, and finished. */
export function patternBars(s: StockSession, t: number, forming: boolean) {
  const capped = s.auctionFrom !== undefined && t > s.auctionFrom;
  return { pt: capped ? s.auctionFrom! : t, forming: capped ? false : forming };
}

export function analyseStock(s: StockSession, t: number, curveFrac: number, forming0 = false, sessionBars = 75): Analysed {
  const b = s.bars;
  const { pt, forming } = patternBars(s, t, forming0);
  const last = b[t - 1];
  const series = [0];
  let hi = -Infinity, lo = Infinity, pv = 0, vv = 0;
  const aboveVwap: boolean[] = [];
  for (let i = 0; i < t; i++) {
    const bar = b[i];
    series.push(pctOf(bar.c, s.prevClose));
    hi = Math.max(hi, bar.h);
    lo = Math.min(lo, bar.l);
    pv += ((bar.h + bar.l + bar.c) / 3) * bar.v;
    vv += bar.v;
    aboveVwap.push(bar.c >= (vv > 0 ? pv / vv : bar.c));
  }
  const pct = series[t];
  const bull = pct > 0;
  const vwapHold = aboveVwap.filter((x) => x === bull).length / t;
  const relVol = s.avgDayVolume > 0 && vv > 0 ? vv / (s.avgDayVolume * curveFrac) : 1;
  const hasOI = s.prevOI > 0;
  const oiChg = hasOI ? pctOf(last.oi, s.prevOI) : 0;
  const buildUp: BuildUp = hasOI ? buildUpOf(pct, oiChg) : 'No F&O data';
  const loc = hi > lo ? (bull ? (last.c - lo) / (hi - lo) : (hi - last.c) / (hi - lo)) : 0.5;
  const oiConf = buildUp === 'Long build-up' || buildUp === 'Short build-up' ? 1 : 0.55;

  // Move size relative to this stock's usual daily range: 2% is big for HDFCBANK, ordinary for IDEA.
  const moveRatio = s.avgDayRangePct && s.avgDayRangePct > 0 ? Math.abs(pct) / s.avgDayRangePct : null;
  const moveNorm = moveRatio !== null ? clamp01(moveRatio / 1.2) : clamp01(Math.abs(pct) / 3);

  // Event risk: an unusual fall, or an opening gap far bigger than the stock's normal day, is
  // usually news. Order-block levels mean little there, so they are withheld.
  const gapPct = pctOf(b[0].o, s.prevClose);
  const usual = s.avgDayRangePct && s.avgDayRangePct > 0 ? s.avgDayRangePct : null;
  // Stocks outside F&O have fixed price bands (5/10/20%); near one, price can freeze at the band.
  const nearBand = !hasOI && [5, 10, 20].some((band) => Math.abs(Math.abs(pct) - band) <= 0.5);
  const eventRisk = nearBand ? `${pct >= 0 ? 'Up' : 'Down'} ${Math.abs(pct).toFixed(1)}%, at or near its price band`
    : usual === null ? null
    : Math.abs(gapPct) >= 1.5 * usual ? `Opened ${gapPct >= 0 ? 'up' : 'down'} ${Math.abs(gapPct).toFixed(1)}%, ${(Math.abs(gapPct) / usual).toFixed(1)}× its usual day: likely news`
    : moveRatio !== null && (bull ? moveRatio >= 3 : moveRatio >= 2.5)
      ? `${bull ? 'Up' : 'Down'} ${Math.abs(pct).toFixed(1)}%, ${moveRatio.toFixed(1)}× its usual day: likely news`
    : null;

  // Strength score, 0–100: how big, how heavy, how clean, and whether new money confirms it.
  const score = Math.round(
    100 * (0.35 * moveNorm + 0.2 * clamp01((relVol - 0.6) / 1.6) + 0.15 * loc + 0.15 * vwapHold + 0.15 * oiConf)
  );

  return {
    symbol: s.symbol, sector: s.sector, prevClose: s.prevClose,
    open: b[0].o, high: hi, low: lo, ltp: last.c, pct, bull, series,
    vwap: vv > 0 ? pv / vv : last.c, vwapHold, relVol, hasOI, oiChg, buildUp, score, moveRatio, eventRisk,
    // Late: confirmed in the last 30 minutes of continuous trading (15:15 for auction stocks).
    obs: orderBlocks(b, pt, forming, usualCandle(s)).map((o) => ({ ...o, late: o.confirmIdx >= (s.auctionFrom ?? sessionBars) - LATE_CANDLES })),
    thin: isThin(s, sessionBars),
    sweeps: sweepsOf(s, b, pt, atrOf(b, pt, last.c), forming),
    flow: flowOf(b, t),
    profile: volumeProfile(b, t, lo, hi),
    sectorAvg: 0, sectorAdv: 0, sectorN: 0, withSector: false,
  };
}

/** Groups analysed stocks into sectors, sides and top-20 lists. */
function assemble(market: Market, t: number, stocks: Analysed[]): Snapshot {
  const bySector = new Map<string, Analysed[]>();
  for (const s of stocks) {
    if (!bySector.has(s.sector)) bySector.set(s.sector, []);
    bySector.get(s.sector)!.push(s);
  }
  const sectors: SectorStat[] = [...bySector.entries()]
    .map(([name, list]) => ({
      name,
      avg: list.reduce((a, s) => a + s.pct, 0) / list.length,
      adv: list.filter((s) => s.bull).length,
      n: list.length,
    }))
    .sort((a, b) => b.avg - a.avg);

  const byScore = (a: Analysed, b: Analysed) => b.score - a.score;
  const bulls = stocks.filter((s) => s.bull).sort(byScore);
  const bears = stocks.filter((s) => !s.bull).sort(byScore);

  return {
    t, time: market.timeLabel(t), stocks, sectors, bulls, bears,
    topBulls: bulls.slice(0, TOP_N), topBears: bears.slice(0, TOP_N),
  };
}

/**
 * Analyse every stock in the session at bar t (1 = first bar complete). `lastBarForming`: bar t-1
 * is still trading (live, during market hours); patterns then use finished candles only.
 */
export function snapshot(market: Market, session: Session, t: number, opts: { lastBarForming?: boolean } = {}): Snapshot {
  const curveFrac = curveFraction(market, t);
  const forming = !!opts.lastBarForming;
  const snap = assemble(market, t, session.stocks.map((s) => analyseStock(s, t, curveFrac, forming, market.bars)));

  // Does the sector back the move? With = sector moving the same way with most of its stocks agreeing.
  const bySector = new Map(snap.sectors.map((x) => [x.name, x]));
  for (const s of snap.stocks) {
    const sec = bySector.get(s.sector)!;
    const share = sec.adv / sec.n;
    s.sectorAvg = sec.avg;
    s.sectorAdv = sec.adv;
    s.sectorN = sec.n;
    s.withSector = s.bull ? sec.avg > 0 && share >= 0.5 : sec.avg <= 0 && share <= 0.5;
  }
  return snap;
}

/** Same snapshot narrowed to one sector, reusing the per-stock analysis. */
export function forSector(market: Market, snap: Snapshot, sector: string): Snapshot {
  return assemble(market, snap.t, snap.stocks.filter((s) => s.sector === sector));
}

/** Swing points: a bar whose high (low) is above (below) the k bars on each side. */
function swings(bars: Bar[], t: number, k = 2) {
  const highs: number[] = [], lows: number[] = [];
  for (let i = k; i < t - k; i++) {
    let isHigh = true, isLow = true;
    for (let j = i - k; j <= i + k; j++) {
      if (j === i) continue;
      if (bars[j].h >= bars[i].h) isHigh = false;
      if (bars[j].l <= bars[i].l) isLow = false;
    }
    if (isHigh) highs.push(bars[i].h);
    if (isLow) lows.push(bars[i].l);
  }
  return { highs, lows };
}

/**
 * Everything the stock page needs beyond the board's per-stock analysis: running VWAP,
 * nearest support/resistance, the active zone, risk, reward:risk and liquidity levels.
 */
export function detail(raw: StockSession, a: Analysed, t: number, allStocks: Analysed[], opts: { lastBarForming?: boolean } = {}): Detail {
  const bars = raw.bars.slice(0, t);
  // Levels, liquidity and the plan read the same bars as the patterns: finished, before the auction.
  const pb = patternBars(raw, t, !!opts.lastBarForming);
  const tp = pb.forming ? pb.pt - 1 : pb.pt;
  const pbars = bars.slice(0, Math.max(1, tp));
  const vwap: number[] = [];
  let pv = 0, vv = 0;
  for (const b of bars) {
    pv += ((b.h + b.l + b.c) / 3) * b.v;
    vv += b.v;
    vwap.push(vv > 0 ? pv / vv : b.c);
  }

  const ltp = a.ltp;
  const atr = pbars.reduce((x, b) => x + (b.h - b.l), 0) / pbars.length || ltp * 0.002;
  const gap = atr * 0.25; // levels closer than this count as one
  const dedupe = (arr: number[]) => arr.filter((p, i) => i === 0 || Math.abs(p - arr[i - 1]) > gap);
  const { highs, lows } = swings(pbars, pbars.length);
  // Levels hugging the current price aren't meaningful support/resistance.
  const res = dedupe([...highs, a.high].filter((p) => p > ltp + gap).sort((x, y) => x - y));
  const sup = dedupe([...lows, a.low].filter((p) => p < ltp - gap).sort((x, y) => y - x));
  // No structure on one side (price at the day's extreme): project two average bar ranges per level.
  const projected = new Set<SRKey>();
  while (res.length < 2) { res.push((res.length ? res[res.length - 1] : ltp) + 2 * atr); projected.add(('R' + res.length) as SRKey); }
  while (sup.length < 2) { sup.push((sup.length ? sup[sup.length - 1] : ltp) - 2 * atr); projected.add(('S' + sup.length) as SRKey); }
  const levels: Record<SRKey, number> = { R1: res[0], R2: res[1], S1: sup[0], S2: sup[1] };

  const active = a.obs.filter((o) => !o.mitigated);
  const zone = active.filter((o) => o.type === (a.bull ? 'bull' : 'bear')).pop() || active.pop() || null;

  // Bullish: the idea is wrong below S1, measured to R2. Bearish: wrong above R1, measured to S2.
  const stop = a.bull ? levels.S1 : levels.R1;
  const target = a.bull ? levels.R2 : levels.S2;
  const riskAbs = Math.abs(ltp - stop);
  const risk = (riskAbs / ltp) * 100;
  const rr = riskAbs > 0 ? Math.abs(target - ltp) / riskAbs : 0;

  // Liquidity: prices where stop-loss and breakout orders tend to cluster.
  const liquidity: Omit<LiquidityLevel, 'distPct'>[] = [];
  if (raw.prevHigh) liquidity.push({ key: 'PDH', name: "yesterday's high", p: raw.prevHigh });
  if (raw.prevLow) liquidity.push({ key: 'PDL', name: "yesterday's low", p: raw.prevLow });
  if (pbars.length >= 3) {
    liquidity.push({ key: 'ORH', name: 'opening-range high (first 15 min)', p: Math.max(...pbars.slice(0, 3).map((b) => b.h)) });
    liquidity.push({ key: 'ORL', name: 'opening-range low (first 15 min)', p: Math.min(...pbars.slice(0, 3).map((b) => b.l)) });
  }
  // Round-number step: the smallest 1×10ⁿ or 5×10ⁿ that is at least 1% of price (₹113 → 5, ₹1,170 → 50).
  let step = 10 ** Math.floor(Math.log10(ltp * 0.01));
  if (step < ltp * 0.01) step *= 5;
  if (step < ltp * 0.01) step *= 2;
  let roundUp = Math.ceil(ltp / step) * step, roundDown = Math.floor(ltp / step) * step;
  if (roundUp - ltp <= gap) roundUp += step; // sitting on a round number: use the next one out
  if (ltp - roundDown <= gap) roundDown -= step;
  liquidity.push({ key: 'RND', name: 'round number', p: roundUp }, { key: 'RND', name: 'round number', p: roundDown });
  // Untaken equal highs/lows: stops still resting just beyond them.
  const eq = equalLevels(pbars, pbars.length, atr);
  for (const h of eq.highs) if (h.untaken && h.p > ltp) liquidity.push({ key: 'EQH', name: 'equal highs (stops above)', p: h.p });
  for (const l of eq.lows) if (l.untaken && l.p < ltp) liquidity.push({ key: 'EQL', name: 'equal lows (stops below)', p: l.p });

  // The plan only uses a zone on the side of today's move: a bearish zone in a rising stock is a
  // different idea, not a plan for this one.
  const sideZone = active.filter((o) => o.type === (a.bull ? 'bull' : 'bear')).pop() ?? null;
  const plan = a.eventRisk || !sideZone ? null : zonePlan(raw, a, pbars, pbars.length, atr, sideZone, eq);

  const below = allStocks.filter((x) => x.score < a.score).length;
  const percentile = (below / Math.max(1, allStocks.length - 1)) * 100;

  return {
    bars, vwap, atr, levels, projected, zone, stop, target, risk, rr, percentile,
    liquidity: liquidity.map((l) => ({ ...l, distPct: ((l.p - ltp) / ltp) * 100 })),
    plan,
  };
}

/**
 * The plan for one zone as the stock page would show it at bar t (whatever its side and the event
 * risk): the same bars, average candle and levels as detail(). For backtests.
 */
export function zonePlanAt(raw: StockSession, a: Analysed, t: number, zone: OrderBlock, opts: { lastBarForming?: boolean } = {}): ZonePlan {
  const pb = patternBars(raw, t, !!opts.lastBarForming);
  const tp = pb.forming ? pb.pt - 1 : pb.pt;
  const pbars = raw.bars.slice(0, Math.max(1, Math.min(t, tp)));
  const atr = pbars.reduce((x, b) => x + (b.h - b.l), 0) / pbars.length || a.ltp * 0.002;
  return zonePlan(raw, a, pbars, pbars.length, atr, zone, equalLevels(pbars, pbars.length, atr));
}

/**
 * The setup if you wait for the zone: enter at Z (the near edge of the block), wrong just beyond
 * its far edge, targets at the next untaken levels ahead (swing highs/lows, the day's extreme,
 * yesterday's and the opening range's high/low, equal highs/lows, an opposing order block).
 */
function zonePlan(raw: StockSession, a: Analysed, bars: Bar[], t: number, atr: number,
  zone: OrderBlock, eq: ReturnType<typeof equalLevels>): ZonePlan {
  const bull = zone.type === 'bull';
  const z = bull ? zone.top : zone.bottom;
  // Wrong just beyond the far edge, but never closer to Z than one average candle: a tighter stop
  // sits inside ordinary 5-minute noise and would make reward:risk look better than it is.
  const buffer = Math.max(atr * 0.1, 0.05);
  const stop = bull ? Math.min(zone.bottom - buffer, z - atr) : Math.max(zone.top + buffer, z + atr);
  const risk = Math.abs(z - stop);

  // Targets lie ahead of both the zone and today's price, at levels price hasn't traded through.
  const ahead = (p: number) => (bull ? p > Math.max(z, a.ltp) + atr * 0.25 : p < Math.min(z, a.ltp) - atr * 0.25);
  const untakenAfter = (p: number, from: number) => {
    for (let j = from; j < t; j++) if (bull ? bars[j].h > p : bars[j].l < p) return false;
    return true;
  };

  const cands: { p: number; name: string }[] = [];
  for (let i = 2; i < t - 2; i++) {
    let ok = true;
    for (let j = i - 2; j <= i + 2 && ok; j++) if (j !== i && (bull ? bars[j].h >= bars[i].h : bars[j].l <= bars[i].l)) ok = false;
    const p = bull ? bars[i].h : bars[i].l;
    if (ok && untakenAfter(p, i + 1)) cands.push({ p, name: bull ? 'swing high' : 'swing low' });
  }
  let extreme = bull ? -Infinity : Infinity;
  for (let j = 0; j < t; j++) extreme = bull ? Math.max(extreme, bars[j].h) : Math.min(extreme, bars[j].l);
  cands.push({ p: extreme, name: bull ? "the day's high" : "the day's low" });
  const prev = bull ? raw.prevHigh : raw.prevLow;
  if (prev && untakenAfter(prev, 0)) cands.push({ p: prev, name: bull ? "yesterday's high" : "yesterday's low" });
  if (t > 3) {
    const or = bull ? Math.max(...bars.slice(0, 3).map((x) => x.h)) : Math.min(...bars.slice(0, 3).map((x) => x.l));
    if (untakenAfter(or, 3)) cands.push({ p: or, name: bull ? 'opening-range high' : 'opening-range low' });
  }
  for (const e of bull ? eq.highs : eq.lows) if (e.untaken) cands.push({ p: e.p, name: bull ? 'equal highs' : 'equal lows' });
  for (const o of a.obs) {
    if (!o.mitigated && o.type !== zone.type) cands.push({ p: bull ? o.bottom : o.top, name: bull ? 'bearish order block' : 'bullish order block' });
  }
  // Nearest first; a target counts only if it is at least one average candle beyond the previous
  // one (two targets a few paise apart are one level). At equal prices the first name listed wins.
  const levels = cands.filter((c) => ahead(c.p)).sort((x, y) => (bull ? x.p - y.p : y.p - x.p));
  const targets: { p: number; name: string }[] = [];
  for (const c of levels) if (!targets.length || Math.abs(c.p - targets[targets.length - 1].p) >= atr) targets.push(c);
  while (targets.length < 2) {
    const from = targets.length ? targets[targets.length - 1].p : bull ? Math.max(z, a.ltp) : Math.min(z, a.ltp);
    targets.push({ p: bull ? from + 2 * atr : from - 2 * atr, name: 'projected' });
  }
  const [t1, t2] = targets;
  // Reward is measured to a real level: T2 when it is one, else T1; with neither, there is none.
  const rrTo: 't1' | 't2' = t2.name !== 'projected' || t1.name === 'projected' ? 't2' : 't1';
  const real = t1.name !== 'projected';
  const rrTarget = rrTo === 't2' ? t2.p : t1.p;
  return {
    side: zone.type, z, stop, t1: t1.p, t2: t2.p, t1Name: t1.name, t2Name: t2.name, riskPerShare: risk,
    rr: real && risk > 0 ? Math.abs(rrTarget - z) / risk : null,
    rrTo,
    distPct: ((a.ltp - z) / a.ltp) * 100,
    inZone: a.ltp <= zone.top && a.ltp >= zone.bottom,
    state: zoneState(zone),
    retests: zone.retests,
  };
}
